import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowLeft,
  Pencil,
  Trash2,
  RotateCcw,
  X,
  Check,
  Receipt,
  ShoppingBag,
  Gift,
  Clock3,
  CreditCard,
  CalendarDays,
  Phone,
  FileText,
  History,
  Package,
  Hourglass,
} from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  useOrder,
  useUpdateOrder,
  useDeleteOrder,
  useRestoreOrder,
  OrderForm,
  TicketModal,
} from "@/features/orders";
import { esIngreso } from "@cold-crew/shared";
import type { OrderCreate, OrderStatus } from "@cold-crew/shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/pedidos/$pedidoId")({
  component: PedidoDetailPage,
});

function fmt(n: number) {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 2,
  }).format(n);
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function TipoBadge({ tipo }: { tipo: string }) {
  const regalo = tipo === "regalo";
  const Icon = regalo ? Gift : ShoppingBag;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold",
        regalo
          ? "bg-[#f1ece5] text-[#5f5a53]"
          : "bg-[#ffe4d3] text-[#c95818]"
      )}
    >
      <Icon className="size-3.5" strokeWidth={2.2} />
      {regalo ? "Regalo" : "Venta"}
    </span>
  );
}

function EstadoBadge({ status }: { status: OrderStatus }) {
  const styles: Record<string, string> = {
    pendiente: "bg-[#fff3c9] text-[#a66b00]",
    entregado: "bg-[#dff3e8] text-[#176c4f]",
    cancelado: "bg-[#fde2df] text-[#b64035]",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold",
        styles[status] ?? "bg-[#efede8] text-[#615d56]"
      )}
    >
      <Clock3 className="size-3.5" strokeWidth={2.2} />
      <span className="capitalize">{status}</span>
    </span>
  );
}

function PagadoBadge({ pagado }: { pagado: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold",
        pagado
          ? "bg-[#dff3e8] text-[#176c4f]"
          : "bg-[#efede8] text-[#615d56]"
      )}
    >
      <CreditCard className="size-3.5" strokeWidth={2.2} />
      {pagado ? "Pagado" : "No pagado"}
    </span>
  );
}

type StockError = {
  message: string;
  faltantes: {
    insumo_nombre: string;
    requerido: number;
    disponible: number;
    unidad: string;
  }[];
};

function QuickStatusUpdate({
  currentStatus,
  onUpdate,
  isLoading,
}: {
  currentStatus: OrderStatus;
  onUpdate: (
    s: OrderStatus
  ) => Promise<void | { stockError?: StockError }>;
  isLoading: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState<OrderStatus>(
    currentStatus === "pagado" ? "pendiente" : currentStatus
  );
  const [stockError, setStockError] = useState<StockError | null>(null);

  async function save() {
    setStockError(null);
    const result = await onUpdate(val);
    if (result?.stockError) {
      setStockError(result.stockError);
      return;
    }
    setOpen(false);
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 transition-opacity hover:opacity-75"
        title="Cambiar estado de entrega"
      >
        <EstadoBadge status={currentStatus} />
        <Pencil className="size-3 text-[#8b867e]" />
      </button>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <select
          value={val}
          onChange={(e) => setVal(e.target.value as OrderStatus)}
          className="h-8 rounded-lg border border-[#ddd8cf] bg-[#fbfaf7] px-2.5 text-xs font-semibold text-[#282622] focus:border-[#f47b2a] focus:outline-none focus:ring-2 focus:ring-[#f47b2a]/15"
        >
          {currentStatus === "entregado" ? (
            <>
              <option value="entregado">Entregado</option>
              <option value="cancelado">Cancelado</option>
            </>
          ) : (
            <>
              <option value="pendiente">Pendiente</option>
              <option value="entregado">Entregado</option>
              <option value="cancelado">Cancelado</option>
            </>
          )}
        </select>

        <button
          onClick={save}
          disabled={isLoading}
          className="flex size-8 items-center justify-center rounded-lg text-[#176c4f] hover:bg-[#dff3e8] disabled:opacity-50"
          title="Guardar"
        >
          <Check className="size-4" />
        </button>

        <button
          onClick={() => {
            setOpen(false);
            setStockError(null);
          }}
          className="flex size-8 items-center justify-center rounded-lg text-[#77726a] hover:bg-[#efede8]"
          title="Cancelar"
        >
          <X className="size-4" />
        </button>
      </div>

      {stockError && (
        <div className="mt-2 max-w-xs rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <p className="mb-1 font-bold">Inventario insuficiente</p>
          <ul className="space-y-0.5">
            {stockError.faltantes.map((f) => (
              <li key={f.insumo_nombre}>
                {f.insumo_nombre}: necesita {f.requerido} {f.unidad}, disponible{" "}
                {f.disponible} {f.unidad}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InfoItem({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof CalendarDays;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f4f1eb] text-[#6f6a62]">
        <Icon className="size-4" strokeWidth={1.9} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-[#8b867e]">{label}</p>
        <p className="mt-0.5 text-[13px] font-bold capitalize leading-snug text-[#24221f]">
          {value}
        </p>
      </div>
    </div>
  );
}

function PedidoDetailPage() {
  const { pedidoId } = Route.useParams();
  const id = Number(pedidoId);
  const navigate = useNavigate();

  const { data: order, isLoading, isError } = useOrder(id);
  const updateOrder = useUpdateOrder(id);
  const deleteOrder = useDeleteOrder();
  const restoreOrder = useRestoreOrder();
  const [editing, setEditing] = useState(false);
  const [showTicket, setShowTicket] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center gap-2 text-sm text-[#77726a]">
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        Cargando pedido…
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3">
        <p className="text-sm text-red-600">No se pudo cargar el pedido.</p>
        <Link
          to="/pedidos"
          className="text-sm font-semibold text-[#f47b2a] hover:underline"
        >
          ← Volver a pedidos
        </Link>
      </div>
    );
  }

  async function handleUpdate(data: OrderCreate) {
    await updateOrder.mutateAsync(data);
    setEditing(false);
  }

  async function handleDelete() {
    if (!confirm("¿Eliminar este pedido?")) return;
    await deleteOrder.mutateAsync(id);
    navigate({ to: "/pedidos" });
  }

  async function handleRestore() {
    await restoreOrder.mutateAsync(id);
  }

  async function handleStatusUpdate(s: OrderStatus) {
    try {
      await updateOrder.mutateAsync({ status: s });
    } catch (err: any) {
      if (err?.code === "STOCK_INSUFICIENTE") {
        return { stockError: err };
      }
      throw err;
    }
  }

  const isDeleted = !!order.deleted_at;
  const subtotal =
    order.detalles?.reduce((s, d) => s + d.subtotal, 0) ?? order.total;
  const esVenta = order.tipo_pedido === "venta";

  if (editing) {
    return (
      <div className="mx-auto max-w-[1280px] space-y-5 p-4 sm:p-5 lg:p-6">
        <PageHeader
          title={`Editar ${order.numero_pedido}`}
          actions={
            <button
              onClick={() => setEditing(false)}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#77726a] transition-colors hover:text-[#171614]"
            >
              <X className="size-4" /> Cancelar edición
            </button>
          }
        />
        <div className="rounded-2xl border border-[#ded9cf] bg-white p-5">
          <OrderForm
            initialValues={order}
            onSubmit={handleUpdate}
            isLoading={updateOrder.isPending}
            submitLabel="Guardar cambios"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#f4f1ea]">
      <main className="mx-auto w-full max-w-[1280px] px-4 py-5 sm:px-5 lg:px-6">
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <Link
              to="/pedidos"
              className="mb-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#77726a] transition-colors hover:text-[#171614]"
            >
              <ArrowLeft className="size-4" />
              Volver
            </Link>

            <h1 className="text-[26px] font-black tracking-[-0.035em] text-[#1d1b19]">
              {order.numero_pedido}
            </h1>
            <p className="mt-1 text-[13px] font-medium text-[#77726a]">
              Cliente:{" "}
              <span className="font-semibold uppercase">
                {order.cliente_nombre}
              </span>
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isDeleted ? (
              <button
                onClick={handleRestore}
                disabled={restoreOrder.isPending}
                className="flex h-10 items-center gap-2 rounded-xl bg-[#176c4f] px-4 text-[12px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                <RotateCcw className="size-4" />
                Restaurar
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowTicket(true)}
                  className="flex h-10 items-center gap-2 rounded-xl border border-[#d8d3ca] bg-white px-4 text-[12px] font-bold text-[#3d3934] transition-colors hover:bg-[#f8f6f1]"
                >
                  <Receipt className="size-4" />
                  Ticket
                </button>

                <button
                  onClick={() => setEditing(true)}
                  className="flex h-10 items-center gap-2 rounded-xl border border-[#d8d3ca] bg-white px-4 text-[12px] font-bold text-[#3d3934] transition-colors hover:bg-[#f8f6f1]"
                >
                  <Pencil className="size-4" />
                  Editar
                </button>

                <button
                  onClick={handleDelete}
                  disabled={deleteOrder.isPending}
                  className="flex h-10 items-center gap-2 rounded-xl border border-[#efb09a] bg-white px-4 text-[12px] font-bold text-[#d45536] transition-colors hover:bg-[#fff3ee] disabled:opacity-50"
                >
                  <Trash2 className="size-4" />
                  Eliminar
                </button>
              </>
            )}
          </div>
        </div>

        {isDeleted && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            Este pedido fue eliminado el {fmtDate(order.deleted_at)}.
          </div>
        )}

        {!isDeleted && !esVenta && (
          <div className="mb-4 rounded-xl border border-[#e3d8cb] bg-[#f5efe8] px-4 py-3 text-sm font-medium text-[#6f6257]">
            🎁 Pedido de regalo — no cuenta como ingreso.
          </div>
        )}

        <section className="overflow-hidden rounded-2xl border border-[#ded9cf] bg-white">
          <div className="grid gap-4 border-b border-dashed border-[#ddd8cf] px-5 py-4 sm:grid-cols-3">
            <div className="flex items-center gap-3 sm:border-r sm:border-[#eeeae2]">
              <span className="text-[13px] font-semibold text-[#6f6a62]">
                Tipo
              </span>
              <TipoBadge tipo={order.tipo_pedido} />
            </div>

            <div className="flex items-center gap-3 sm:border-r sm:border-[#eeeae2] sm:px-2">
              <span className="text-[13px] font-semibold text-[#6f6a62]">
                Entrega
              </span>
              {isDeleted ? (
                <EstadoBadge status={order.status} />
              ) : (
                <QuickStatusUpdate
                  currentStatus={order.status}
                  onUpdate={handleStatusUpdate}
                  isLoading={updateOrder.isPending}
                />
              )}
            </div>

            <div className="flex items-center gap-3 sm:pl-2">
              <span className="text-[13px] font-semibold text-[#6f6a62]">
                Pago
              </span>
              {isDeleted ? (
                <PagadoBadge pagado={order.pagado} />
              ) : (
                <button
                  onClick={() =>
                    updateOrder.mutateAsync({ pagado: !order.pagado })
                  }
                  disabled={updateOrder.isPending}
                  className="flex items-center gap-1.5 transition-opacity hover:opacity-75 disabled:opacity-50"
                  title="Cambiar estado de pago"
                >
                  <PagadoBadge pagado={order.pagado} />
                  <Pencil className="size-3 text-[#8b867e]" />
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-x-8 gap-y-5 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3">
            <InfoItem
              icon={CalendarDays}
              label="Fecha pedido"
              value={fmtDate(order.fecha_pedido)}
            />
            <InfoItem
              icon={CalendarDays}
              label="Fecha entrega"
              value={fmtDate(order.fecha_entrega)}
            />
            <InfoItem
              icon={CreditCard}
              label="Método de pago"
              value={
                order.metodo_pago === "n/a"
                  ? "N/A (Regalo)"
                  : order.metodo_pago
              }
            />
            <InfoItem
              icon={Phone}
              label="Contacto"
              value={order.cliente_contacto ?? "—"}
            />
            <InfoItem
              icon={FileText}
              label="Creado"
              value={fmtDate(order.created_at)}
            />
            <InfoItem
              icon={History}
              label="Actualizado"
              value={fmtDate(order.updated_at)}
            />
          </div>

          <div className="mx-4 mb-4 flex items-center gap-3 rounded-xl bg-[#fff6ef] px-4 py-3 text-[12px] font-medium text-[#665f58]">
            <Hourglass className="size-4 shrink-0 text-[#f47b2a]" />
            <span>
              {esIngreso(order)
                ? "Este pedido cuenta como ingreso."
                : order.tipo_pedido === "regalo"
                  ? "Pedido de regalo — excluido de ingresos."
                  : "No cuenta como ingreso hasta que esté pagado."}
            </span>
          </div>

          {order.notas && (
            <div className="border-t border-[#eeeae2] px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#99948b]">
                Notas
              </p>
              <p className="mt-1 text-[13px] font-medium text-[#3d3934]">
                {order.notas}
              </p>
            </div>
          )}
        </section>

        {order.detalles && order.detalles.length > 0 && (
          <section className="mt-4 overflow-hidden rounded-2xl border border-[#ded9cf] bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <Package className="size-5 text-[#f47b2a]" strokeWidth={2.2} />
              <h2 className="text-[16px] font-black text-[#1d1b19]">
                Productos ({order.detalles.length})
              </h2>
            </div>

            <div className="overflow-hidden rounded-xl border border-[#ebe6dd]">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-[13px]">
                  <thead>
                    <tr className="bg-[#f8f6f1]">
                      {["Producto", "Cant.", "Precio unit.", "Subtotal"].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-left text-[11px] font-bold text-[#77726a]"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#eeeae2]">
                    {order.detalles.map((d) => (
                      <tr
                        key={d.id}
                        className="transition-colors hover:bg-[#fbfaf7]"
                      >
                        <td className="px-4 py-3.5 font-bold text-[#282622]">
                          {d.producto_nombre ??
                            `Producto #${d.producto_id}`}
                          {d.notas && (
                            <span className="mt-0.5 block text-[11px] font-normal text-[#8b867e]">
                              {d.notas}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 tabular-nums text-[#4d4943]">
                          {d.cantidad}
                        </td>
                        <td className="px-4 py-3.5 tabular-nums text-[#4d4943]">
                          {fmt(d.precio_unitario)}
                        </td>
                        <td className="px-4 py-3.5 font-black tabular-nums text-[#282622]">
                          {fmt(d.subtotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-3 flex justify-end rounded-xl bg-[#fff8f3] px-5 py-4">
              <div className="w-full max-w-[310px]">
                <div className="flex items-center justify-between text-[13px]">
                  <span className="font-medium text-[#77726a]">Subtotal</span>
                  <span className="font-bold tabular-nums text-[#282622]">
                    {fmt(subtotal)}
                  </span>
                </div>

                {order.descuento > 0 && (
                  <div className="mt-2 flex items-center justify-between text-[13px] text-[#176c4f]">
                    <span className="font-semibold">Descuento</span>
                    <span className="font-bold tabular-nums">
                      -{fmt(order.descuento)}
                    </span>
                  </div>
                )}

                <div
                  className={cn(
                    "mt-3 flex items-end justify-between border-t border-dashed border-[#ddd8cf] pt-3",
                    !esVenta && "text-[#6f6257]"
                  )}
                >
                  <span className="text-[15px] font-black">Total</span>
                  <span className="text-[28px] font-black leading-none tracking-[-0.04em] tabular-nums">
                    {fmt(order.total)}
                  </span>
                </div>

                {!esVenta && (
                  <p className="mt-2 text-right text-[10px] font-medium text-[#8b867e]">
                    Total calculado — no cuenta como ingreso
                  </p>
                )}
              </div>
            </div>
          </section>
        )}

        {showTicket && (
          <TicketModal order={order} onClose={() => setShowTicket(false)} />
        )}
      </main>
    </div>
  );
}
